import {
  ListParams,
  PositionTemplateFieldType,
  Requests,
  Resources,
} from '@erecruit/types';
import {
  ContactRequestWithCompaniesContext,
  ResourceRequestWithPreferences,
  ClientProjectRequestWithCompaniesContext,
  OpportunityRequestWithCompaniesContext,
  AgreementRequestWithCompaniescontext,
  ShiftTypeRequestWithCompaniesContext,
  WorksiteRequestWithCompaniesContext,
  FacilityDepartmentRequestWithCompaniesContext,
} from '@erecruit/modules';
import {
  PositionField,
  PositionTemplate,
  FormFieldType,
} from '../../AddNewPositionWizard/types';

type DataSourceMap = {
  [K in PositionTemplateFieldType]?: ResourceRequestWithPreferences;
};

export interface Sections {
  general: PositionTemplateFieldType[];
  location: PositionTemplateFieldType[];
  schedule: PositionTemplateFieldType[];
  review: PositionTemplateFieldType[];
  billing: PositionTemplateFieldType[];
}

const sections: Sections = {
  general: [
    PositionTemplateFieldType.PositionTitle,
    PositionTemplateFieldType.PositionType,
    PositionTemplateFieldType.PositionDescription,
    PositionTemplateFieldType.NumberOfPositions,
    PositionTemplateFieldType.Agreement,
    PositionTemplateFieldType.MaxNumberOfSubmittals,
    PositionTemplateFieldType.Opportunity,
    PositionTemplateFieldType.PrimaryDepartment,
    PositionTemplateFieldType.ReportsTo,
    PositionTemplateFieldType.SubmittalLimitPerVendor,
    PositionTemplateFieldType.Supervisor,
    PositionTemplateFieldType.TravelPercentage,
    PositionTemplateFieldType.ClientProject,
    PositionTemplateFieldType.FolderGroup,
  ],
  location: [
    PositionTemplateFieldType.WorksiteAddress,
    PositionTemplateFieldType.FacilityDepartments,
    PositionTemplateFieldType.WorkFromHome,
  ],
  schedule: [
    PositionTemplateFieldType.ShiftTypes,
    PositionTemplateFieldType.StartTime,
    PositionTemplateFieldType.EndTime,
    PositionTemplateFieldType.EstimatedStartDate,
    PositionTemplateFieldType.EstimatedEndDate,
    PositionTemplateFieldType.Duration,
  ],
  review: [
    PositionTemplateFieldType.PositionCreationReason,
    PositionTemplateFieldType.PositionCreationReasonNote,
    PositionTemplateFieldType.GeneralComments,
  ],
  billing: [
    PositionTemplateFieldType.RequestFunding,
    PositionTemplateFieldType.RequestFundingDetails,
  ],
};

export const numberFields = [
  PositionTemplateFieldType.NumberOfPositions,
  PositionTemplateFieldType.MaxNumberOfSubmittals,
];

export const singleSelectFields = [
  PositionTemplateFieldType.PositionType,
  PositionTemplateFieldType.WorksiteAddress,
  PositionTemplateFieldType.Agreement,
  PositionTemplateFieldType.Opportunity,
  PositionTemplateFieldType.PrimaryDepartment,
  PositionTemplateFieldType.ReportsTo,
  PositionTemplateFieldType.Supervisor,
  PositionTemplateFieldType.ClientProject,
  PositionTemplateFieldType.PositionCreationReason,
  PositionTemplateFieldType.FolderGroup,
  PositionTemplateFieldType.ShiftTypes,
];

export const multiSelectFields = [
  PositionTemplateFieldType.FacilityDepartments,
];

export const datepickerFields = [
  PositionTemplateFieldType.StartTime,
  PositionTemplateFieldType.EndTime,
  PositionTemplateFieldType.EstimatedStartDate,
  PositionTemplateFieldType.EstimatedEndDate,
];

export const getDataSourceMap = (): DataSourceMap => {
  const dataSourceMap = {
    [PositionTemplateFieldType.WorksiteAddress]: new WorksiteRequestWithCompaniesContext(
      {},
    ),
    [PositionTemplateFieldType.PositionType]: new Requests.PositionTypeRequest(
      {},
    ),
    [PositionTemplateFieldType.FacilityDepartments]: new FacilityDepartmentRequestWithCompaniesContext(
      {},
    ),
    [PositionTemplateFieldType.ShiftTypes]: new ShiftTypeRequestWithCompaniesContext(
      {},
    ),
    [PositionTemplateFieldType.Agreement]: new AgreementRequestWithCompaniescontext(
      {},
    ),
    [PositionTemplateFieldType.Opportunity]: new OpportunityRequestWithCompaniesContext(
      {},
    ),
    [PositionTemplateFieldType.PrimaryDepartment]: new Requests.DepartmentRequest(
      {},
    ),
    [PositionTemplateFieldType.ReportsTo]: new ContactRequestWithCompaniesContext(
      {},
    ),
    [PositionTemplateFieldType.Supervisor]: new ContactRequestWithCompaniesContext(
      {},
    ),
    [PositionTemplateFieldType.PositionCreationReason]: new Requests.PositionCreationReasonRequest(
      {},
    ),
    [PositionTemplateFieldType.FolderGroup]: new Requests.FolderGroupRequest(
      {},
    ),
    [PositionTemplateFieldType.ClientProject]: new ClientProjectRequestWithCompaniesContext(
      {},
    ),
  };

  return dataSourceMap;
};

const getSection = (fieldType: PositionTemplateFieldType) => {
  return Object.keys(sections).find(
    key => sections[key as keyof Sections].indexOf(fieldType) !== -1,
  ) as keyof Sections;
};

const getFormFieldType = (fieldType: PositionTemplateFieldType) => {
  if (singleSelectFields.indexOf(fieldType) !== -1) {
    return FormFieldType.SingleSelect;
  }

  if (multiSelectFields.indexOf(fieldType) !== -1) {
    return FormFieldType.MultiSelect;
  }

  return FormFieldType.Text;
};

const transformField = (field: Resources.TemplateField): PositionField => {
  const { fieldType, settings } = field;
  const section = getSection(fieldType);
  const formFieldType = getFormFieldType(fieldType);

  let { value } = field;

  //@TODO REMOVE WEIRD LOGIC ONCE CONTRACT IS UPDATED
  if (fieldType === PositionTemplateFieldType.ShiftTypes) {
    value = value && value.length ? value[0] : undefined;
  }

  return {
    fieldType,
    formFieldType,
    settings,
    value,
    section,
  };
};

export const getSelectFields = (fields: PositionField[]): PositionField[] => {
  return fields.filter(field => {
    return (
      field.formFieldType === FormFieldType.SingleSelect ||
      field.formFieldType === FormFieldType.MultiSelect
    );
  });
};

export const getDataSourceForField = (
  field: PositionField,
): ResourceRequestWithPreferences => {
  const dataSourceMap = getDataSourceMap();
  const dataSource = dataSourceMap[
    field.fieldType
  ] as ResourceRequestWithPreferences;
  if (field.fieldType === PositionTemplateFieldType.WorksiteAddress) {
    const worksiteAddressValue = field.value?.id || (field.value as number);

    const params: ListParams<Resources.WorksiteFilter> = {
      pager: {
        offset: 0,
        limit: 10,
      },
    };

    if (worksiteAddressValue) {
      params.filter = {
        ids: [worksiteAddressValue],
      };
    }

    dataSource.resourceParams = params;
  }
  return dataSource;
};

export const transformPositionTemplate = (
  template: Resources.PositionTemplateDetails,
): PositionTemplate => {
  const fields = template.fields.map(tf => transformField(tf));

  return {
    ...template,
    fields,
  };
};
